/**
 * SNAP-2308 — explicit URL-protocol allowlist for the editor's Link mark (ASVS 1.2.2).
 *
 * ## What was actually wrong
 *
 * The finding said `Link.configure({ openOnClick: false })` has "no `protocols` restriction",
 * and recommended `protocols: ['http', 'https', 'mailto']`. Both halves are wrong for the
 * version of Tiptap this app runs (`@tiptap/extension-link` 3.x, upgraded in SNAP-2143):
 *
 *  - Tiptap 3 ships a default `isAllowedUri` that ALREADY rejects `javascript:`, `data:`,
 *    `vbscript:`, `blob:` and `file:` at `setLink`, at `parseHTML` and at `renderHTML`, after
 *    stripping the DOMPurify unicode-whitespace set (so `java\tscript:` cannot slip past).
 *    The reported `javascript:` payload is not reproducible on `development`.
 *  - The `protocols` option does NOT narrow anything. Read it in the extension source: it
 *    PUSHES onto the built-in allowlist and calls linkifyjs' `registerCustomProtocol` for each
 *    entry. Applying the recommendation verbatim would have widened the surface (and
 *    registered `http`/`https`/`mailto` as linkify "custom" protocols) for zero benefit.
 *
 * `isAllowedUri` is the only option that actually restricts, so the policy lives here.
 *
 * ## What this adds
 *
 * The safety of the editor should not rest on an undocumented library default that already
 * changed shape once between Tiptap 2 and 3 (the extension still carries a `// TODO: v4`
 * note). This pins the allowlist explicitly, and narrows Tiptap's default set —
 * `http, https, ftp, ftps, mailto, tel, callto, sms, cid, xmpp` — to the three protocols the
 * product actually emits and consumes. Nothing in `src/` produces a `tel:`, `sms:`, `ftp:`,
 * `callto:`, `cid:` or `xmpp:` link.
 *
 * ## Relationship to the render-side sanitizer
 *
 * `src/helpers/sanitizeHtml.ts` (SNAP-2297) runs DOMPurify on the READ path, whose default
 * `ALLOWED_URI_REGEXP` permits `ftp, ftps, http, https, mailto, tel, callto, sms, cid, xmpp,
 * matrix`. This list is deliberately a STRICT SUBSET of that one: input is tighter than
 * render, never the other way round. Keep it that way — a protocol allowed here but not there
 * would be accepted by the editor and then stripped when the article is displayed.
 *
 * Restricting at INPUT (and letting Tiptap's `parseHTML` drop the mark while keeping the text)
 * is what keeps already-stored articles readable: a legacy link whose protocol is no longer
 * allowed loses its anchor, not its words.
 */

/** Protocols the editor will attach to a link, without the trailing colon. Lowercase. */
export const ALLOWED_LINK_PROTOCOLS: readonly string[] = ['http', 'https', 'mailto'];

/**
 * Characters DOMPurify (and Tiptap) strip before testing a URI, so that `java&#9;script:`
 * and friends cannot smuggle a blocked scheme past a naive `startsWith` check.
 * Mirrors UNICODE_WHITESPACE_PATTERN in `@tiptap/extension-link/src/helpers/whitespace.ts`.
 */
// eslint-disable-next-line no-control-regex -- matching control characters IS the point: they are what hides a blocked scheme.
const UNICODE_WHITESPACE_GLOBAL = /[\u0000-\u0020\u00A0\u1680\u180E\u2000-\u2029\u205F\u3000]/g;

/**
 * A leading RFC-3986 scheme. Anything that does not match this is a relative path, an
 * anchor (`#section`), a protocol-relative URL (`//host/x`) or a bare word — all of which the
 * editor supports and none of which can carry script.
 */
const LEADING_SCHEME = /^([a-z][a-z0-9+.-]*):/i;

/**
 * True when `url` may be used as a link href.
 *
 * Empty input is allowed: Tiptap probes `renderHTML` with an absent href while building marks,
 * and returning false there would strip legitimate links.
 */
export const isAllowedLinkUri = (url: string | null | undefined): boolean => {
  if (!url) {
    return true;
  }

  const scheme = url.replace(UNICODE_WHITESPACE_GLOBAL, '').match(LEADING_SCHEME);

  // No scheme at all — relative, anchor, or protocol-relative. Safe by construction.
  if (!scheme) {
    return true;
  }

  return ALLOWED_LINK_PROTOCOLS.indexOf(scheme[1].toLowerCase()) !== -1;
};
